import { useEffect, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { SectionLabel } from "./sharedUI";

const FAQ_ITEMS = [
  { q: "Is there a setup fee?", a: "No. Every ReadyTrades plan is one flat monthly payment — no build fee, no upfront design fee, no domain fee." },
  { q: "How quickly will my website be ready?", a: "Most sites launch within 7–14 working days from when we receive your information." },
  { q: "Who owns my domain?", a: "You do. The domain is registered in your business's name and stays with you if you ever leave." },
  { q: "Can I request changes after we launch?", a: "Yes. Every plan includes small updates each month; larger changes are quoted separately." },
];

export const FAQSection = () => {
  const [openFaq, setOpenFaq] = useState(0);

  // Inject FAQ structured data for SEO. FAQ_ITEMS is a stable module-level constant
  // so we intentionally run this once on mount.
  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": FAQ_ITEMS.map((f) => ({
        "@type": "Question", "name": f.q,
        "acceptedAnswer": { "@type": "Answer", "text": f.a },
      })),
    });
    document.head.appendChild(script);
    return () => { script.remove(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section id="faqs" className="py-20 lg:py-32 border-b border-[color:var(--border-soft)]" data-testid="faq-section">
      <div className="max-w-[1280px] mx-auto px-6 lg:px-10">
        <SectionLabel n="06" name="FAQs / Sample" />
        <div className="grid grid-cols-12 gap-8 lg:gap-14">
          <div className="col-span-12 lg:col-span-4">
            <h2 className="h-lg text-[color:var(--ink)]">
              A few reasonable <span className="font-serif-italic">questions</span>.
            </h2>
            <p className="text-[15px] text-[color:var(--muted)] mt-6 max-w-sm">
              Full FAQ appears in the final build.
            </p>
          </div>
          <div className="col-span-12 lg:col-span-8">
            <div className="border-t border-[color:var(--border)]">
              {FAQ_ITEMS.map((f, i) => {
                const open = openFaq === i;
                return (
                  <div key={f.q} className="border-b border-[color:var(--border-soft)]" data-testid={`faq-${i}`}>
                    <button
                      onClick={() => setOpenFaq(open ? -1 : i)}
                      className="w-full flex items-start justify-between gap-6 py-5 text-left"
                      aria-expanded={open}
                    >
                      <div className="flex items-start gap-4">
                        <span className="grid-ref pt-1">0{i + 1}</span>
                        <span className="font-display text-[18px] lg:text-[22px] text-[color:var(--ink)] leading-tight">{f.q}</span>
                      </div>
                      <span className="flex-shrink-0 mt-1">
                        {open ? <Minus size={16} strokeWidth={2} /> : <Plus size={16} strokeWidth={2} />}
                      </span>
                    </button>
                    {open && (
                      <div className="pb-6 pl-12 pr-10 text-[15px] leading-[1.65] text-[color:var(--muted)]">
                        {f.a}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
